import { resolveTranslation } from '@/utils/resolveTranslation';
import { translateSectionsLabel } from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';

interface SectionNavProps {
  sections: Array<{ id: string; label: string }>;
  activeId: string;
  className?: string;
  /**
   * TELV1-360 — project analysis language for the "Sections" heading.
   * Default 'English' preserves backwards-compat with any out-of-tree
   * callers; in-app callers (Overview, Questions) pass through the
   * project's `analysis_language`.
   */
  analysisLanguage?: string;
}

/** Compact nav for jumping between analysis sections. Designed to sit
 *  in a sticky sidebar alongside stats cards. Tracks the active section
 *  via a highlighted left border in brand green. */
function SectionNav({
  sections,
  activeId,
  className = '',
  analysisLanguage = 'English',
}: SectionNavProps) {
  if (sections.length <= 1) return null;

  return (
    <nav
      className={`flex flex-col gap-0.5 rounded-xl border border-neutral-200/80 bg-white shadow-sm p-3 ${className}`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 px-2.5 mb-1">
        {resolveTranslation(translateSectionsLabel, analysisLanguage)}
      </span>
      {sections.map((section) => {
        const isActive = section.id === activeId;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => {
              document.getElementById(section.id)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
            className={`text-left text-xs leading-tight py-1.5 px-2.5 rounded-md border-l-2 transition-colors ${
              isActive
                ? 'border-l-primary text-primary font-medium bg-primary/5'
                : 'border-l-transparent text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            {section.label}
          </button>
        );
      })}
    </nav>
  );
}

export default SectionNav;
